import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../Components/UiComponents/Table/Table";
import Loader from "react-spinner-loader";
import { getDisasterAlertsHistory } from "../../../Services/EndPoints";
import { getData } from "../../../Services/Services";
import { useDispatch } from "react-redux";
import { setTableData } from "../../../CentralStore/ExportLMSTable/dataSlice";
import { getExportedColumnsData, toDisplayRegionName } from "../../../Components/UiComponents/utils";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Bell,
  Clock,
  CheckCircle,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Activity,
  Droplets,
  Filter,
  CloudRain,
  MountainSnow,
  Download,
  Sheet,
} from "lucide-react";


// Define export columns configuration
const disasterAlertHistoryColumnsForExport = [
  { key: "disasterAlertId", label: "Alert ID" },
  { key: "regionName", label: "Region" },
  { key: "hazardType", label: "Disaster" },
  { key: "severity", label: "Severity" },
  { key: "measuredValue", label: "Measured Value" },
  { key: "thresholdValue", label: "Threshold Value" },
  { key: "unit", label: "Unit" },
  { key: "notificationTimestamp", label: "Incident Date & Time" },
  { key: "acknowledged", label: "Status" },
  { key: "latitude", label: "Latitude" },
  { key: "longitude", label: "Longitude" },
  { key: "alertDescription", label: "Description" },
];

const DisasterAlertHistoryTable = (props) => {
  // Loading state for API calls
  const [loader, setLoader] = useState(false);

  // Main disaster alert data array
  const [resourceData, setResourceData] = useState([]);
  // Filtered disaster alert data based on search/filter
  const [resourceSrchdData, setResourceSrchdData] = useState(null);
  // Total count of filtered rows
  const [rowCount, setRowCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  console.log("reeeeee", resourceData)
   console.log("reeeeee1", isRefreshing)
  // Filter states
  const [selectedSeverity, setSelectedSeverity] = useState("");
  const [selectedHazardType, setSelectedHazardType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedCard, setSelectedCard] = useState("total"); // New state for card selection

  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [valueRange, setValueRange] = useState({ min: "", max: "" });
  const [searchText, setSearchText] = useState("");
  const [appliedDateRange, setAppliedDateRange] = useState({ from: null, to: null });
  const [appliedValueRange, setAppliedValueRange] = useState({ min: "", max: "" });

  const navigate = useNavigate();
  const dispatch = useDispatch();


  // Search data for table filtering
  const [searchData, setSearchData] = useState([]);

  // KPI cards should reflect the applied date range (Apply button), same as
  // the table below — previously they always summarized the full unfiltered
  // resourceData, so the date filter visibly changed the table but not the
  // cards above it.
  const dateFilteredResourceData = useMemo(() => {
    let filtered = resourceData;

    if (appliedDateRange.from) {
      const fromDate = new Date(appliedDateRange.from);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter((alert) => {
        const alertDate = convertTimestampArrayToDate(alert.notificationTimestamp);
        return alertDate && alertDate >= fromDate;
      });
    }

    if (appliedDateRange.to) {
      const toDate = new Date(appliedDateRange.to);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((alert) => {
        const alertDate = convertTimestampArrayToDate(alert.notificationTimestamp);
        return alertDate && alertDate <= toDate;
      });
    }

    return filtered;
  }, [resourceData, appliedDateRange]);

  // In summaryStats calculation (around line 68), add:
  const summaryStats = useMemo(() => {
    return {
      total: dateFilteredResourceData.length,
      pending: dateFilteredResourceData.filter(alert => !alert.acknowledged).length,
      acknowledged: dateFilteredResourceData.filter(alert => alert.acknowledged).length,
      critical: dateFilteredResourceData.filter(alert => alert.severity === "Critical").length,
      high: dateFilteredResourceData.filter(alert => alert.severity === "High").length,
      warning: dateFilteredResourceData.filter(alert => alert.severity === "Warning").length,
      medium: dateFilteredResourceData.filter(alert => alert.severity === "Medium").length,
      low: dateFilteredResourceData.filter(alert => alert.severity === "Low").length,
      // coldWave: dateFilteredResourceData.filter(alert => alert.hazardType === "Cold Wave").length,
      cloudburst: dateFilteredResourceData.filter(alert => alert.hazardType === "Cloud Burst").length,
      earthquake: dateFilteredResourceData.filter(alert => alert.hazardType === "Earthquake").length,
      flood: dateFilteredResourceData.filter(alert => alert.hazardType === "Flood").length,
      avalanche: dateFilteredResourceData.filter(alert => alert.hazardType === "Avalanche").length, // ADD THIS LINE
    };
  }, [dateFilteredResourceData]);

  // In handleCardClick function (around line 95), add this case:
  const handleCardClick = (cardType) => {
    console.log("cardtype", cardType)
    setSelectedCard(cardType);
    setSelectedSeverity("");
    setSelectedHazardType("");
    setSelectedStatus("");

    switch (cardType) {
      case "total":
        break;
      case "pending":
        setSelectedStatus("Pending");
        break;
      case "acknowledged":
        setSelectedStatus("Acknowledged");
        break;
      case "critical":
        setSelectedSeverity("Critical");
        break;
      case "high":
        setSelectedSeverity("High");
        break;
      case "warning":
        setSelectedSeverity("Warning");
        break;
      case "medium":
        setSelectedSeverity("Medium");
        break;
      case "low":
        setSelectedSeverity("Low");
        break;
      // case "coldwave":
      //   setSelectedHazardType("Cold Wave");
      //   break;
      case "cloudburst": // Changed from coldwave to cloudburst
        setSelectedHazardType("Cloud Burst");
        break;
      case "earthquake":
        setSelectedHazardType("Earthquake");
        break;
      case "flood":
        setSelectedHazardType("Flood");
        break;
      case "avalanche":  // ADD THIS CASE
        setSelectedHazardType("Avalanche");
        break;
      default:
        break;
    }
  };
  // Format date for Excel export (dd-mm-yyyy HH:mm:ss)
  const formatDateForExport = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
      let date;

      if (typeof timestamp === 'string') {
        date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'N/A';
      } else if (Array.isArray(timestamp) && timestamp.length >= 6) {
        const [year, month, day, hour, minute, second] = timestamp;
        date = new Date(year, month - 1, day, hour, minute, second);
      } else {
        return 'N/A';
      }

      const d = String(date.getDate()).padStart(2, '0');
      const mo = String(date.getMonth() + 1).padStart(2, '0');
      const y = date.getFullYear();
      const h = String(date.getHours()).padStart(2, '0');
      const mi = String(date.getMinutes()).padStart(2, '0');
      const s = String(date.getSeconds()).padStart(2, '0');

      return `${d}-${mo}-${y} ${h}:${mi}:${s}`;
    } catch (error) {
      console.error('Error formatting date for export:', error);
      return 'N/A';
    }
  };
  // Send data to central store for export functionality
  const sendDataToCentralStore = (data) => {
    if (data && data.length > 0) {
      const columns = disasterAlertHistoryColumnsForExport;
      const formattedData = data.map(item => ({
        disasterAlertId: item.alertSeqId || 'N/A',
        regionName: item.regionName || 'N/A',
        hazardType: item.hazardType || 'N/A',
        severity: item.severity || 'N/A',
        measuredValue: item.measuredValue !== null && item.measuredValue !== undefined ? item.measuredValue : 'N/A',
        thresholdValue: item.thresholdValue !== null && item.thresholdValue !== undefined ? item.thresholdValue : 'N/A',
        unit: item.unit || '',
        notificationTimestamp: item.severity === "Warning" ? "Not Yet Occurred" : formatDateForExport(item.createdAt),
        acknowledged: getStatusDisplay(item.acknowledged),
        latitude: item.latitude !== null && item.latitude !== undefined ? item.latitude : 'N/A',
        longitude: item.longitude !== null && item.longitude !== undefined ? item.longitude : 'N/A',
        alertDescription: item.alertDescription || 'N/A'
      }));
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, formattedData),
        })
      );
    }
  };

  // Download report as Excel — unchanged from before; only the trigger
  // button's look/position changed, not this export logic.
  const handleDownloadReport = async () => {
    const exportData = resourceSrchdData || resourceData;
    if (!exportData || exportData.length === 0) {
      toast.error("No data available to export", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Alert History");

    worksheet.columns = disasterAlertHistoryColumnsForExport.map(c => ({
      header: c.label,
      key: c.key,
      width: 22,
    }));

    // Style header row
    worksheet.getRow(1).eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1D3557' } };
      cell.alignment = { horizontal: 'left', vertical: 'middle' };
    });

    exportData.forEach(item => {
      worksheet.addRow({
        disasterAlertId: item.alertSeqId || item.disasterAlertId || 'N/A',
        regionName: item.regionName ? toDisplayRegionName(item.regionName) : 'N/A',
        hazardType: item.hazardType || 'N/A',
        severity: item.severity || 'N/A',
        measuredValue: item.measuredValue !== null && item.measuredValue !== undefined ? `${item.measuredValue} ${item.unit || ''}`.trim() : 'N/A',
        thresholdValue: item.thresholdValue !== null && item.thresholdValue !== undefined ? `${item.thresholdValue} ${item.unit || ''}`.trim() : 'N/A',
        unit: item.unit || '',
        notificationTimestamp: item.severity === "Warning" ? "Not Yet Occurred" : formatDateForExport(item.createdAt),
        acknowledged: item.acknowledged ? 'Acknowledged' : 'Pending',
        latitude: item.latitude !== null && item.latitude !== undefined ? item.latitude : 'N/A',
        longitude: item.longitude !== null && item.longitude !== undefined ? item.longitude : 'N/A',
        alertDescription: item.alertDescription || 'N/A',
      });
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        row.eachCell(cell => {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        });
      }
    });

    const now = new Date();
    now.setMinutes(now.getMinutes() + 330);
    const ts = now.toISOString().replace('T', '_').split('.')[0].replace(/:/g, '');
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Alert_History_${ts}.xlsx`);
  };

  // Apply filters button handler
  const applyFilters = () => {
    // Validate date range
    if (dateRange.to && !dateRange.from) {
      toast.error("Please select a From Date.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
      return;
    }
    if (dateRange.from && dateRange.to && dateRange.from > dateRange.to) {
      toast.error("From Date must be less than or equal to To Date.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
      return;
    }

    setAppliedDateRange(dateRange);
    setAppliedValueRange(valueRange);
    toast.success("Filters applied successfully", {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
      icon: <CheckCircle2 size={24} color="#10B981" strokeWidth={2.5} />,
      style: {
        background: '#FFFFFF',
        color: '#0F2D42',
        fontFamily: "'Inter','Segoe UI',sans-serif",
        fontSize: '13px',
        fontWeight: '700',
        borderRadius: '16px',
        boxShadow: '0 8px 30px rgba(15,45,66,.12)',
        border: '1px solid rgba(42,101,145,.10)',
        padding: '12px 20px',
        marginTop: '20px',
      },
      progressStyle: { background: '#10B981' },
    });
  };

  // Reset just the date-range filter (From/To + Apply row) back to unfiltered
  const resetDateFilters = () => {
    setDateRange({ from: null, to: null });
    setAppliedDateRange({ from: null, to: null });
    toast.success("Date filter reset", {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
      icon: <CheckCircle2 size={24} color="#10B981" strokeWidth={2.5} />,
      style: {
        background: '#FFFFFF',
        color: '#0F2D42',
        fontFamily: "'Inter','Segoe UI',sans-serif",
        fontSize: '13px',
        fontWeight: '700',
        borderRadius: '16px',
        boxShadow: '0 8px 30px rgba(15,45,66,.12)',
        border: '1px solid rgba(42,101,145,.10)',
        padding: '12px 20px',
        marginTop: '20px',
      },
      progressStyle: { background: '#10B981' },
    });
  };

  // Update central store when search data changes
  useEffect(() => {
    if (searchData && searchData.length > 0) {
      sendDataToCentralStore(searchData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  // Re-filter data when filters or resource data changes
  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeverity, selectedHazardType, selectedStatus, appliedDateRange, appliedValueRange, searchText, resourceData]);

  // Filter disaster alert data based on selected filters
  const filterData = () => {
    let filtered = [...resourceData];

    // Filter by severity
    if (selectedSeverity) {
      filtered = filtered.filter((alert) => alert.severity === selectedSeverity);
    }

    // Filter by hazard type
    if (selectedHazardType) {
      filtered = filtered.filter((alert) => alert.hazardType === selectedHazardType);
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter((alert) => {
        if (selectedStatus === "Acknowledged") {
          return alert.acknowledged === true;
        } else if (selectedStatus === "Pending") {
          return alert.acknowledged === false;
        }
        return true;
      });
    }

    // Filter by date range
    if (appliedDateRange.from) {
      filtered = filtered.filter((alert) => {
        const alertDate = convertTimestampArrayToDate(alert.notificationTimestamp);
        const fromDate = new Date(appliedDateRange.from);
        fromDate.setHours(0, 0, 0, 0);
        return alertDate >= fromDate;
      });
    }

    if (appliedDateRange.to) {
      filtered = filtered.filter((alert) => {
        const alertDate = convertTimestampArrayToDate(alert.notificationTimestamp);
        const toDate = new Date(appliedDateRange.to);
        toDate.setHours(23, 59, 59, 999);
        return alertDate <= toDate;
      });
    }

    // Filter by measured value range
    if (appliedValueRange.min) {
      const minValue = parseFloat(appliedValueRange.min);
      if (!isNaN(minValue)) {
        filtered = filtered.filter((alert) => {
          const alertValue = parseFloat(alert.measuredValue) || 0;
          return alertValue >= minValue;
        });
      }
    }

    if (appliedValueRange.max) {
      const maxValue = parseFloat(appliedValueRange.max);
      if (!isNaN(maxValue)) {
        filtered = filtered.filter((alert) => {
          const alertValue = parseFloat(alert.measuredValue) || 0;
          return alertValue <= maxValue;
        });
      }
    }

    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((alert) =>
        alert.regionName?.toLowerCase().includes(searchLower) ||
        alert.disasterAlertId?.toString().includes(searchLower) ||
        alert.hazardType?.toLowerCase().includes(searchLower) ||
        alert.alertDescription?.toLowerCase().includes(searchLower) ||
        alert.severity?.toLowerCase().includes(searchLower) ||
        getStatusDisplay(alert.acknowledged).toLowerCase().includes(searchLower)
      );
    }

    setResourceSrchdData(filtered);
    setRowCount(filtered.length);
  };

  const resetFilters = () => {
    setSelectedSeverity("");
    setSelectedHazardType("");
    setSelectedStatus("");
    setValueRange({ min: "", max: "" });
    setAppliedValueRange({ min: "", max: "" });
    setSearchText("");
    setSelectedCard("total");
  };

  // Map severity to priority (matching Header.js)
  const mapSeverityToPriority = (severity) => {
    if (!severity) return "MEDIUM";
    // Handle "Level X – Name" format (e.g. "Level 4 – Critical", "Level 2 – Moderate")
    const parts = severity.split('–');
    const coreSeverity = parts.length > 1 ? parts[parts.length - 1].trim() : severity.trim();
    const severityMap = {
      "Critical": "CRITICAL",
      "Warning": "Warning",
      "Medium": "MEDIUM",
      "Moderate": "MEDIUM",
      "High": "HIGH",
      "Low": "LOW"
    };
    return severityMap[coreSeverity] || "MEDIUM";
  };

  // Get emoji based on severity (matching Header.js)
  const getSeverityEmoji = (severity) => {
    const emojiMap = {
      "Critical": "🚨",
      "High": "⚠️",
      "Medium": "⚡",
      "Low": "ℹ️"
    };
    return emojiMap[severity] || "📢";
  };

  // Initialize component - load disaster alert data. Also re-runs whenever
  // refreshAt changes (e.g. after navigating here from Create Alert right
  // after a successful submit), so the newly created alert shows up even if
  // this screen happened to already be mounted rather than freshly loaded.
  useEffect(() => {
    getDisasterAlertsList();

    // Coming straight from a just-created alert: the backend can take a
    // moment to make it queryable through the list API, so the immediate
    // fetch above sometimes lands just before it's visible. A single
    // follow-up fetch shortly after picks it up without the user having to
    // navigate away and back to see it.
    if (props.refreshAt) {
      const retryId = setTimeout(() => {
        getDisasterAlertsList();
      }, 2000);
      return () => clearTimeout(retryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refreshAt]);

  // Handle view icon click
  const handleViewAlertDetails = (rowData) => {
    console.log("rowdata", rowData)
    const serializableNotification = {
      id: rowData.id,
      caseId: `${rowData.disasterAlertId}`,
      notificationTitle: `${getSeverityEmoji(rowData.severity)} ${rowData.hazardType}`,
      notificationMessage: rowData.notificationMessage || rowData.alertDescription,
      createdAt: convertTimestampArrayToDate(rowData.createdAt),
      isRead: rowData.isMarkedAsRead || rowData.markedAsRead || false,
      priority: mapSeverityToPriority(rowData.severity),
      acknowledged: rowData.acknowledged || false,
      acknowledgedName: rowData.acknowledgedName,
      sensorNetwork: rowData.sensorNetwork,
      acktime: rowData.acknowledgedTimestamp,
      disasterType: rowData.hazardType,
      location: rowData.regionName,
      actionTaken: rowData.actionTaken,
      measuredValue: rowData.measuredValue,
      thresholdValue: rowData.thresholdValue,
      unit: rowData.unit,
      alertDescription: rowData.alertDescription,
      severity: rowData.severity,
      latitude: rowData.latitude || 34.1526,
      longitude: rowData.longitude || 77.5771,
      affectedArea: rowData.affectedArea || "N/A",
      affectedPopulation: rowData.affectedPopulation || "N/A",
      reportedBy: rowData.reportedBy || "Disaster Management System",
      contact: rowData.emergencyContact || "+91 1982-XXXXX",
      disasterAlertId: rowData.disasterAlertId,
      assessments: rowData.assessments || [],
      // Only present on alerts created via the Create Alert form's Sensor
      // source (see CreateAlert.js's buildAlertJson) — undefined for
      // anything else, so the section reading these on the detail page
      // stays hidden for those.
      rawAlertTitle: rowData.alertTitle,
      sourceDetails: rowData.sourceDetails,
      onset: rowData.onset,
      effective: rowData.effective,
      expires: rowData.expires,
      regionLocation: rowData.regionLocation,
      // SACHET_CAP / SACHET_EARTHQUAKE = NDMA, SENSOR = Manual (see
      // AccidentViewer.js's getAlertTypeBadge).
      sourceType: rowData.sourceType,
    };

    navigate('/Disaster-Acknowladgement', {
      state: {
        notificationData: serializableNotification,
        allNotifications: resourceData.map(alert => ({
          id: alert.id,
          caseId: `${alert.disasterAlertId}`,
          notificationTitle: `${getSeverityEmoji(alert.severity)} ${alert.hazardType}`,
          notificationMessage: alert.notificationMessage || alert.alertDescription,
          createdAt: convertTimestampArrayToDate(alert.createdAt),
          isRead: alert.isMarkedAsRead || alert.markedAsRead || false,
          priority: mapSeverityToPriority(alert.severity),
          acknowledged: alert.acknowledged || false,
          acknowledgedName: alert.acknowledgedName,
          acktime: alert.acknowledgedTimestamp,
          disasterType: alert.hazardType,
          location: alert.regionName,
          measuredValue: alert.measuredValue,
          thresholdValue: alert.thresholdValue,
          unit: alert.unit,
          actionTaken: alert.actionTaken,
          alertDescription: alert.alertDescription,
          severity: alert.severity,
          latitude: alert.latitude || 34.1526,
          longitude: alert.longitude || 77.5771,
          affectedArea: alert.affectedArea || "N/A",
          affectedPopulation: alert.affectedPopulation || "N/A",
          reportedBy: alert.reportedBy || "Disaster Management System",
          contact: alert.emergencyContact || "+91 1982-XXXXX",
          rawAlertTitle: alert.alertTitle,
          sourceDetails: alert.sourceDetails,
          onset: alert.onset,
          effective: alert.effective,
          expires: alert.expires,
          regionLocation: alert.regionLocation,
          sourceType: alert.sourceType,
        }))
      },
    });
  };

  // Convert timestamp array to Date object
  // Declared as a function statement (hoisted) so it can be called from
  // summaryStats above, which runs on first render before this line
  // is reached in a plain const/arrow declaration.
  function convertTimestampArrayToDate(timestamp) {
    if (!timestamp) return null;

    try {
      // Handle ISO string e.g. "2026-03-23T11:25:55.122581"
      if (typeof timestamp === 'string') {
        const date = new Date(timestamp);
        return isNaN(date.getTime()) ? null : date;
      }

      // Handle array format e.g. [2026, 3, 23, 11, 25, 55, ...]
      if (Array.isArray(timestamp) && timestamp.length >= 6) {
        const [year, month, day, hour, minute, second] = timestamp;
        return new Date(year, month - 1, day, hour, minute, second);
      }

      return null;
    } catch (error) {
      console.error('Error converting timestamp:', error);
      return null;
    }
  };

  // Fetch disaster alerts list
  const getDisasterAlertsList = async () => {
    setLoader(true);
    setIsRefreshing(true);

    try {
      const response = await getData(getDisasterAlertsHistory);

      if (!response || !response.notifications || !Array.isArray(response.notifications)) {
        console.error("Invalid response structure:", response);
        toast.error("Invalid data format received", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        setLoader(false);
        setIsRefreshing(false);

        return;
      }

      const alertList = response.notifications;

      const transformedData = alertList.map(alert => ({
        ...alert,
        disasterAlertId: alert.alertSeqId || 'N/A',
        hazardType: alert.hazardType || 'Unknown',
        severity: alert.severity || 'N/A',
        regionName: alert.regionName || 'N/A',
        measuredValue: alert.measuredValue !== null && alert.measuredValue !== undefined ? alert.measuredValue : null,
        thresholdValue: alert.thresholdValue !== null && alert.thresholdValue !== undefined ? alert.thresholdValue : null,
        unit: alert.unit || '',
        alertDescription: alert.alertDescription || 'N/A',
        notificationMessage: alert.notificationMessage || '',
        actionTaken: alert.actionTaken,
        notificationTimestamp: alert.notificationTimestamp || null,
        acknowledged: alert.acknowledged !== undefined ? alert.acknowledged : false,
        latitude: alert.latitude || null,
        longitude: alert.longitude || null,
        status: getStatusDisplay(alert.acknowledged)
      }));

      const sortedData = transformedData.sort((a, b) => {
        const dateA = convertTimestampArrayToDate(a.notificationTimestamp);
        const dateB = convertTimestampArrayToDate(b.notificationTimestamp);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB - dateA;
      });

      setLoader(false);
      setIsRefreshing(false);
      setResourceData(sortedData);
      setRowCount(sortedData.length);

      sendDataToCentralStore(sortedData);
    } catch (error) {
      setLoader(false);
      setIsRefreshing(false);
      console.error("Error fetching disaster alerts list:", error);
      toast.error("Failed to fetch disaster alerts list", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Get status display text
  const getStatusDisplay = (acknowledged) => {
    return acknowledged ? 'Acknowledged' : 'Pending';
  };


  // Format date from timestamp array to "dd-mm-yy, time" format
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
      let date;

      // Handle ISO string e.g. "2026-03-23T11:25:55.122581"
      if (typeof timestamp === 'string') {
        date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'N/A';
      }
      // Handle array format e.g. [2026, 3, 23, 11, 25, 55]
      else if (Array.isArray(timestamp) && timestamp.length >= 6) {
        const [year, month, day, hour, minute, second] = timestamp;
        date = new Date(year, month - 1, day, hour, minute, second);
      } else {
        return 'N/A';
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      let hours = date.getHours();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const mins = String(date.getMinutes()).padStart(2, '0');

      return `${day}-${month}-${year}, ${String(hours).padStart(2, '0')}:${mins} ${ampm}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Extract core severity name from "Level X – Name" or plain "Name" format
  const getCoreSeverityName = (severity) => {
    if (!severity) return '';
    const parts = severity.split('–');
    return parts.length > 1 ? parts[parts.length - 1].trim() : severity.trim();
  };

  // Get severity background color
  const getSeverityColor = (severity) => {
    const core = getCoreSeverityName(severity);
    const colorMap = {
      'Critical': '#8B0000',
      'High': '#F44336',
      'Major': '#F44336',
      'Moderate': '#FF9800',
      'Medium': '#FF9800',
      'Warning': '#FFC107',
      'Minor': '#4CAF50',
      'Low': '#4CAF50'
    };
    return colorMap[core] || colorMap[severity] || '#6c757d';
  };

  // Get severity text color (dark for yellow, white for others)
  const getSeverityTextColor = (severity) => {
    const core = getCoreSeverityName(severity);
    return core === 'Warning' ? '#212121' : '#ffffff';
  };

  // Get status color
  const getStatusColor = (acknowledged) => {
    return acknowledged ? '#28a745' : '#ffc107';
  };

  // Status icon component
  const StatusIcon = ({ acknowledged }) => {
    const color = getStatusColor(acknowledged);
    const status = getStatusDisplay(acknowledged);

    if (acknowledged) {
      return (
        <CheckCircle2
          size={18}
          color={color}
          style={{ strokeWidth: 2 }}
          title={status}
        />
      );
    } else {
      return (
        <Clock
          size={18}
          color={color}
          style={{ strokeWidth: 2 }}
          title={status}
        />
      );
    }
  };

  // Summary Card Component
  // Summary Card Component
  const SummaryCard = ({ icon: Icon, iconBg, iconColor, count, label, cardType, isActive }) => {
    // Check if icon is a string (emoji) or a component
    const isEmoji = typeof Icon === 'string';

    return (
      <div
        className={`summary-card ${isActive ? 'active' : ''}`}
        onClick={() => handleCardClick(cardType)}
        style={{ cursor: 'pointer' }}
      >
        <div className="card-icon" style={{ backgroundColor: iconBg }}>
          {isEmoji ? (
            <span style={{ fontSize: '24px' }}>{Icon}</span>
          ) : (
            <Icon size={24} color={iconColor || '#495057'} />
          )}
        </div>
        <div className="card-content">
          <h3>{count}</h3>
          <p>{label}</p>
        </div>
      </div>
    );
  };

  // Define table columns
  const columns = useMemo(
    () => [
      {
        Header: "Alert ID",
        accessor: "alertSeqId",
        disableSortBy: true,
        Cell: ({ value }) => {
          return value ? <span style={{ fontWeight: 'bold' }} title={value}>{value}</span> : "N/A";
        },
      },
      {
        Header: "Region",
        accessor: "regionName",
        disableSortBy: true,
        Cell: ({ value }) => {
          return value ? <span title={value}>{toDisplayRegionName(value)}</span> : "N/A";
        },
      },
      {
        Header: "Disaster",
        accessor: "hazardType",
        disableSortBy: true,
        Cell: ({ value }) => {
          return <span title={value}>{value}</span>;
        },
      },
      {
        Header: "Severity",
        accessor: "severity",
        disableSortBy: true,
        Cell: ({ value }) => {
          const bgColor = getSeverityColor(value);
          const textColor = getSeverityTextColor(value);

          return (
            <span
              className="badge"
              style={{
                backgroundColor: bgColor,
                color: textColor,
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              {value}
            </span>
          );
        },
      },
      {
        Header: "Threshold",
        accessor: "thresholdValue",
        disableSortBy: true,
        Cell: ({ value, row }) => {
          const unit = row.original.unit || '';
          return value !== null && value !== undefined ?
            <span title={`${value} ${unit}`}>{`${value} ${unit}`}</span> : "N/A";
        },
      },
      {
        Header: "Measured Value",
        accessor: "measuredValue",
        disableSortBy: true,
        Cell: ({ value, row }) => {
          const unit = row.original.unit || '';
          return value !== null && value !== undefined ?
            <span title={`${value} ${unit}`}>{`${value} ${unit}`}</span> : "N/A";
        },
      },
      {
        Header: "Incident Date & Time",
        accessor: "createdAt",
        disableSortBy: true,
        Cell: ({ value, row }) => {
          // Hide / NA for Warning severity
          if (row.original.severity === "Level 0 – Warning") {
            return <span>Not Yet Occurred</span>;
          }

          const formattedDate = formatDateTime(value);
          return <span title={formattedDate}>{formattedDate}</span>;
        },
      },
      {
        Header: "Status",
        // A plain accessor here would give react-table's global search box the
        // raw boolean ("true"/"false") to match against instead of the
        // "Pending"/"Acknowledged" text actually shown — so accessor returns
        // the display string for searching, while Cell reads the real
        // boolean off row.original for the icon/color.
        accessor: (row) => getStatusDisplay(row.acknowledged),
        disableSortBy: true,
        Cell: ({ row }) => {
          const acknowledged = row.original.acknowledged;
          const displayStatus = getStatusDisplay(acknowledged);
          const color = getStatusColor(acknowledged);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <StatusIcon acknowledged={acknowledged} />
              <span style={{ color: color, fontWeight: '500' }}>{displayStatus}</span>
            </div>
          );
        },
      },
      {
        Header: "Actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                className="btn btn-sm"
                onClick={() => handleViewAlertDetails(row.original)}
                title="View Details"
              >
                <i className="fas fa-eye"></i>
              </button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  // Filter section component
  const FiltersSection = () => (
    <div className="card mb-4 filters-card" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
      {/* Filters Heading */}
      <div className="filters-header">
        <div className="filters-title-container">
          <Filter size={20} color="#495057" />
          <h5 className="filters-title">Filters</h5>
        </div>
      </div>

      <div className="card-body" style={{ padding: '12px', paddingTop: '16px' }}>
        {/* Single Row: Date Range, Apply, Reset, Severity, Status, Hazard */}
        <div className="row g-2 align-items-end">

          {/* Hazard Type Filter */}
          <div className="col">
            <label className="form-label small text-muted">Disaster</label>
            <select
              className="form-select"
              value={selectedHazardType}
              onChange={(e) => setSelectedHazardType(e.target.value)}
              style={{
                borderRadius: '6px',
                height: '35px',
                padding: '0 8px',
                fontSize: '13px'
              }}
            >
              <option value="">All</option>
              <option value="Flood">Flood</option>
              <option value="Cloud Burst">Cloudburst</option>
              <option value="Avalanche">Avalanche</option>
              <option value="Earthquake">Earthquake</option>
            </select>
          </div>

          {/* Severity Filter */}
          <div className="col">
            <label className="form-label small text-muted">Severity</label>
            <select
              className="form-select"
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              style={{
                borderRadius: '6px',
                height: '35px',
                padding: '0 8px',
                fontSize: '13px'
              }}
            >
              <option value="">All</option>

              <option value="Level 0 – Warning">Level 0 – Warning</option>
              <option value="Level 1 – Minor">Level 1 – Minor</option>
              <option value="Level 2 – Moderate">Level 2 – Moderate</option>
              <option value="Level 3 – Major">Level 3 – Major</option>
              <option value="Level 4 – Critical">Level 4 – Critical</option>


            </select>
          </div>

          {/* Status Filter */}
          <div className="col">
            <label className="form-label small text-muted">Status</label>
            <select
              className="form-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{
                borderRadius: '6px',
                height: '35px',
                padding: '0 8px',
                fontSize: '13px'
              }}
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Acknowledged">Acknowledged</option>
            </select>
          </div>
          {/* Reset Button - Smaller */}
          <div className="col-auto">
            <label className="form-label small text-muted">&nbsp;</label>
            <button
              className="btn filter-reset-btn"
              onClick={resetFilters}
              style={{ minWidth: '70px' }}
            >
              <i className="fas fa-sync-alt" style={{ marginRight: '4px', fontSize: '12px' }}></i>
              Reset
            </button>
          </div>
          {/* Refresh Button */}
          <div className="col-auto">
            <label className="form-label small text-muted">&nbsp;</label>
            <button
              className="btn filter-refresh-btn"
              onClick={getDisasterAlertsList}
              disabled={isRefreshing}
              style={{ minWidth: '80px' }}
              title="Click to Refresh the data"
            >
              <i className={`fas fa-redo-alt${isRefreshing ? ' fa-spin' : ''}`} style={{ marginRight: '4px', fontSize: '12px' }}></i>
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid" style={{ fontFamily: "'Inter','Segoe UI',sans-serif", background: '#F0F6FB', minHeight: '100vh', padding: 0 }}>
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Disaster Alert Data"
      />

      {/* ── NAV ── */}
      <nav className="pda-nav">
        <div className="pda-nav-brand">
          {/* <button
            className="pda-back-btn"
            onClick={() => navigate(-1)}
            title="Go Back"
          >
            <ArrowLeft size={18} color="white" strokeWidth={2.5} />
          </button> */}
          <div className="pda-nav-logo">
            <AlertCircle size={20} color="white" strokeWidth={2} />
          </div>
          <div className="pda-nav-title">Pre-Disaster Alert Report</div>
        </div>

        <div className="pda-nav-right">
          <div className="pda-daterange">
            <div className="pda-daterange-field">
              <span className="pda-dr-label">From</span>
              <DatePicker
                selected={dateRange.from}
                onChange={(date) => {
                  setDateRange(prev => ({
                    from: date,
                    // clear To if: From was cleared, or new From is after existing To
                    to: (!date || (prev.to && date > prev.to)) ? null : prev.to
                  }));
                }}
                maxDate={dateRange.to || new Date()}
                dateFormat="dd/MM/yyyy"
                className="pda-dp-input"
                placeholderText="Select date"
                onKeyDown={(e) => e.preventDefault()}
                onChangeRaw={(e) => e.preventDefault()}
                autoComplete="off"
                isClearable={true}
              />
            </div>
            <div className="pda-dr-divider" />
            <div className="pda-daterange-field">
              <span className="pda-dr-label">To</span>
              <DatePicker
                selected={dateRange.to}
                onChange={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                minDate={dateRange.from || undefined}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                className="pda-dp-input"
                placeholderText="Select date"
                disabled={!dateRange.from}
                onKeyDown={(e) => e.preventDefault()}
                onChangeRaw={(e) => e.preventDefault()}
                autoComplete="off"
                isClearable={true}
              />
            </div>
          </div>
          <button
            className="pda-btn-apply"
            onClick={applyFilters}
            disabled={!(dateRange.from && dateRange.to && dateRange.from <= dateRange.to)}
            title={!(dateRange.from && dateRange.to) ? "Please select both From and To dates" : ""}
          >Apply</button>
          <button
            className="pda-btn-reset"
            onClick={resetDateFilters}
            disabled={!(dateRange.from || dateRange.to || appliedDateRange.from || appliedDateRange.to)}
            title="Clear the date filter"
          >Reset</button>
        </div>
      </nav>

      <div className="create-user p-4" style={{ marginTop: '0px' }}>


        {/* Summary Cards Section */}
        <div className="summary-cards-container" style={{ marginTop: '20px' }}>
          {/* First Row - Status Cards */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <SummaryCard
                icon={Bell}
                iconBg="#EBF4FA"
                iconColor="#2A6591"
                count={summaryStats.total}
                label="Total Alerts"
                cardType="total"
                isActive={selectedCard === "total"}
              />
            </div>
            <div className="col-md-4">
              <SummaryCard
                icon={Clock}
                iconBg="#FFF4E5"
                iconColor="#D97706"
                count={summaryStats.pending}
                label="Pending"
                cardType="pending"
                isActive={selectedCard === "pending"}
              />
            </div>
            <div className="col-md-4">
              <SummaryCard
                icon={CheckCircle}
                iconColor="#059669"
                iconBg="#D1FAE5"
                count={summaryStats.acknowledged}
                label="Acknowledged"
                cardType="acknowledged"
                isActive={selectedCard === "acknowledged"}
              />
            </div>
          </div>

          {/* Second Row - Hazard Types */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <SummaryCard
                icon={Droplets}
                iconBg="#EBF4FA"
                iconColor="#3680B5"
                count={summaryStats.flood}
                label="Flood"
                cardType="flood"
                isActive={selectedCard === "flood"}
              />
            </div>
            <div className="col-md-3">
              <SummaryCard
                icon={CloudRain}
                iconBg="#EBF4FA"
                iconColor="#2A6591"
                count={summaryStats.cloudburst} // Changed from coldWave to cloudburst
                label="Cloudburst" // Changed from Cold Wave to Cloudburst
                cardType="cloudburst" // Changed from coldwave to cloudburst
                isActive={selectedCard === "cloudburst"} // Changed from coldwave to cloudburst
              />
            </div>
            <div className="col-md-3">
              <SummaryCard
                icon={MountainSnow}
                iconBg="#EBF4FA"
                iconColor="#245A85"
                count={summaryStats.avalanche}
                label="Avalanche"
                cardType="avalanche"
                isActive={selectedCard === "avalanche"}
              />
            </div>
            <div className="col-md-3">
              <SummaryCard
                icon={Activity}
                iconBg="#EBF4FA"
                iconColor="#1C4E72"
                count={summaryStats.earthquake}
                label="Earthquake"
                cardType="earthquake"
                isActive={selectedCard === "earthquake"}
              />
            </div>
          </div>
        </div>

        <FiltersSection />

        <div className="datatable__container pda-history-table" style={{ marginTop: "-24px", paddingBottom: '16px', position: "relative" }}>
          {resourceData.length > 0 && (
            <button
              onClick={handleDownloadReport}
              title="Export to Excel"
              style={{
                position: "absolute",
                top: "14px",
                right: "20px",
                zIndex: 5,
                padding: "8px 12px",
                backgroundColor: "#10B981",
                color: "white",
                border: "none",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                fontWeight: "500",
                height: "36px",
                fontSize: "12px",
              }}
            >
              <Download size={14} />
              <Sheet size={14} />
            </button>
          )}
          {resourceData.length > 0 ? (
            <Table
              data={resourceSrchdData || resourceData}
              columns={columns}
              numberOfRows={10}
              showExport={false}
              setSearchData={setSearchData}
              showFilter={false}
              showReset={false}
              onExpand={false}
              rowCount={rowCount}
              searchPlaceholder="Search by Alert ID, Region, Disaster, Severity, Status..."
              headerTableHeadStyle={{
                background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)",
                color: "#FFFFFF",
              }}
              tableStyle={{
                border: "1px solid #f0f0f0",
              }}
            />
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-exclamation-triangle fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No Disaster Alert Data Available</h5>
            </div>
          )}
        </div>
      </div>

      <style>
        {`
        /* ── Modern Header Navbar ── */
        .pda-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 16px;
          background: linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%);
          color: #fff;
          font-family: 'Sora', sans-serif;
          position: relative;
          height: 56px;
        }
        .pda-nav-brand {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .pda-back-btn {
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 8px;
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s;
        }
        .pda-back-btn:hover {
          background: rgba(255,255,255,0.22);
        }
        .pda-nav-logo {
          width: 32px;
          height: 32px;
          background: rgba(255,255,255,.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .pda-nav-title {
          font-size: 16px;
          font-weight: 700;
          letter-spacing: -0.2px;
        }
        .pda-nav-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .pda-daterange {
          display: flex;
          align-items: center;
          background: #fff;
          border-radius: 8px;
          height: 36px;
          overflow: visible;
          box-shadow: 0 1px 4px rgba(0,0,0,0.12);
        }
        .pda-daterange-field {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 0 12px;
          position: relative;
          height: 100%;
        }
        .pda-dr-label {
          font-size: .6rem;
          font-weight: 700;
          color: #7A9BB0;
          text-transform: uppercase;
          letter-spacing: .08em;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .pda-dp-input {
          background: transparent !important;
          border: none !important;
          outline: none !important;
          font-family: 'Inter','Segoe UI',sans-serif !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          color: #0F2D42 !important;
          width: 108px !important;
          cursor: pointer !important;
          padding-right: 22px !important;
        }
        .pda-dp-input::placeholder {
          color: #A0AEC0 !important;
          font-weight: 400 !important;
        }
        .pda-dp-input:disabled {
          cursor: not-allowed !important;
          color: #C0C0C0 !important;
        }
        /* Style for react-datepicker clear icon */
        .pda-daterange-field .react-datepicker__close-icon {
          position: absolute !important;
          right: 2px !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          padding: 0 !important;
          margin: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          height: 100% !important;
          width: 20px !important;
          background: transparent !important;
        }
        .pda-daterange-field .react-datepicker-wrapper {
          width: 115px;
        }
        .pda-daterange-field .react-datepicker__close-icon::after {
          content: "\\00d7" !important;
          background: #CBD5E1 !important;
          color: #475569 !important;
          font-size: 10px !important;
          width: 14px !important;
          height: 14px !important;
          border-radius: 50% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          line-height: 1 !important;
          cursor: pointer !important;
        }
        .pda-dr-divider {
          width: 1px;
          height: 20px;
          background: #E2E8F0;
          flex-shrink: 0;
        }
        .pda-btn-apply {
          height: 34px;
          padding: 0 20px;
          background: #fff;
          color: #2A6591;
          border: none;
          border-radius: 9px;
          font-size: .78rem;
          font-weight: 700;
          font-family: 'Inter','Segoe UI',sans-serif;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(15,45,66,.12);
          transition: opacity .14s, transform .12s;
          margin-left: 4px;
        }
        .pda-btn-apply:hover:not(:disabled) {
          opacity: .9;
          transform: translateY(-1px);
        }
        .pda-btn-apply:active {
          transform: none;
        }
        .pda-btn-apply:disabled {
          background: rgba(255,255,255,0.18);
          color: rgba(255,255,255,0.5);
          border: 1px solid rgba(255,255,255,0.35);
          cursor: not-allowed;
          box-shadow: none;
          opacity: 1;
        }
        .pda-btn-reset {
          height: 34px;
          padding: 0 20px;
          background: transparent;
          color: #fff;
          border: 1px solid rgba(255,255,255,0.6);
          border-radius: 9px;
          font-size: .78rem;
          font-weight: 700;
          font-family: 'Inter','Segoe UI',sans-serif;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
          transition: opacity .14s, transform .12s, background .14s;
          margin-left: 8px;
        }
        .pda-btn-reset:hover:not(:disabled) {
          background: rgba(255,255,255,0.12);
          transform: translateY(-1px);
        }
        .pda-btn-reset:active {
          transform: none;
        }
        .pda-btn-reset:disabled {
          color: rgba(255,255,255,0.4);
          border: 1px solid rgba(255,255,255,0.25);
          cursor: not-allowed;
        }

        /* ── DMS Header — Avalanche nav style ─────────────────────── */
        .lms__header {
          background: #1C4E72 !important;
          color: #fff !important;
          border-radius: 0 !important;
          padding: 0 28px !important;
          height: 56px !important;
          box-shadow: 0 2px 20px rgba(15,45,66,.18) !important;
          position: sticky !important;
          top: 0 !important;
          z-index: 100 !important;
        }

        .lms__title {
          font-family: 'Sora','Segoe UI',sans-serif !important;
          font-size: .88rem !important;
          font-weight: 700 !important;
          color: #fff !important;
          letter-spacing: -0.01em !important;
        }

        .lms__title svg {
          color: rgba(255,255,255,.8) !important;
          stroke: rgba(255,255,255,.8) !important;
        }

        .lms__title_icon {
          color: rgba(255,255,255,.8) !important;
        }

        .lms__title_icon svg,
        .lms__title_icon path {
          color: rgba(255,255,255,.8) !important;
          fill: rgba(255,255,255,.8) !important;
        }

        /* ── Avalanche-themed Summary Cards (HLD compact sizing) ─── */
        .summary-card {
          background: #FFFFFF;
          border-radius: 12px;
          padding: 10px 10px;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 0 2px 4px rgba(15,45,66,.04), 0 8px 28px rgba(42,101,145,.08);
          transition: all 0.25s cubic-bezier(.16,1,.3,1);
          border: 1.5px solid rgba(42,101,145,.08);
          position: relative;
          overflow: hidden;
          cursor: pointer;
        }

        .summary-card:hover {
          border-color: rgba(42,101,145,.18);
          box-shadow: 0 4px 16px rgba(42,101,145,.1);
          transform: translateY(-2px);
        }

        .summary-card:active {
          transform: translateY(-1px);
        }

        .summary-card.active {
          border-color: #2A6591;
          box-shadow: 0 4px 20px rgba(42,101,145,.16);
          background: linear-gradient(135deg, rgba(42,101,145,.05), transparent);
        }

        .summary-card .card-icon {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          transition: all 0.25s cubic-bezier(.34,1.56,.64,1);
          box-shadow: 0 2px 6px rgba(0,0,0,.06);
          flex-shrink: 0;
          position: relative;
        }

        .summary-card .card-icon::after {
          content: ''; position: absolute; inset: 0;
          border-radius: inherit;
          border: 1px solid rgba(0,0,0,.05);
          pointer-events: none;
        }

        .summary-card:hover .card-icon {
          transform: scale(1.1);
        }

        .summary-card.active .card-icon {
          transform: scale(1.06);
          box-shadow: 0 4px 14px rgba(42,101,145,.14);
        }

        .summary-card .card-content h3 {
          margin: 0;
          font-family: 'Inter','Segoe UI',sans-serif;
          font-size: .78rem;
          font-weight: 700;
          color: #2A6591;
          letter-spacing: -0.01em;
          background: none;
          -webkit-background-clip: unset;
          -webkit-text-fill-color: #2A6591;
          background-clip: unset;
        }

        .summary-card:hover .card-content h3 {
          transform: none;
        }

        .summary-card.active .card-content h3 {
          color: #1C4E72;
          -webkit-text-fill-color: #1C4E72;
          font-weight: 800;
        }

        .summary-card .card-content p {
          margin: 2px 0 0;
          font-family: 'Inter','Segoe UI',sans-serif;
          font-size: .70rem;
          color: #3A5F78;
          font-weight: 600;
          letter-spacing: -0.01em;
          line-height: 1.3;
          word-break: break-word;
          transition: color 0.2s;
        }

        .summary-card:hover .card-content p {
          color: #0F2D42;
        }

        .summary-card.active .card-content p {
          font-weight: 700;
          color: #0B2236;
        }

        /* ── Filters Section ─────────────────────────────────────────── */
        .filters-card {
          box-shadow: 0 2px 4px rgba(15,45,66,.04), 0 8px 28px rgba(42,101,145,.08);
          position: relative;
          z-index: 100;
          overflow: visible !important;
          border: 1px solid rgba(42,101,145,.10) !important;
          border-radius: 18px !important;
        }

        .filters-header {
          background: linear-gradient(135deg, #F5FBFF 0%, #EBF4FA 100%);
          padding: 14px 18px;
          border-bottom: 1px solid rgba(42,101,145,.07);
          border-radius: 18px 18px 0 0;
        }

        .filters-title-container {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .filters-title {
          margin: 0;
          font-family: 'Sora','Segoe UI',sans-serif;
          font-size: .92rem;
          font-weight: 700;
          color: #0F2D42;
          letter-spacing: -0.01em;
        }

        /* ── Layout ───────────────────────────────────────────────── */
        .grid-stack>.grid-stack-item>.grid-stack-item-content {
          margin: 0;
          position: absolute;
          width: auto;
          overflow-y: visible !important;
        }

        .datatable__container {
          margin-bottom: 12px !important;
          padding-bottom: 12px !important;
          overflow: visible !important;
          position: relative;
          z-index: 1;
        }

        /* Make room for the Export to Excel button so it sits to the
           right of the table's built-in search box instead of overlapping it. */
        .pda-history-table .table-search {
          margin-right: 72px;
        }

        /* Align the table's left/right edges with the Filters section above
           it — the shared Table component adds a 1.3em side margin by default. */
        .pda-history-table .iccc__table--container {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }

        .create-user {
          overflow: visible !important;
        }

        .container-fluid {
          overflow: visible !important;
        }

        /* ── Form Controls ────────────────────────────────────────── */
        .form-select,
        .form-control {
          font-family: 'Inter','Segoe UI',sans-serif !important;
          font-size: .78rem !important;
          border: 1px solid rgba(42,101,145,.15) !important;
          border-radius: 10px !important;
          color: #0F2D42 !important;
          transition: border-color .15s, box-shadow .15s !important;
        }

        .form-select:focus,
        .form-control:focus {
          border-color: #2A6591 !important;
          box-shadow: 0 0 0 3px rgba(42,101,145,.10) !important;
        }

        .form-label {
          font-family: 'Inter','Segoe UI',sans-serif !important;
          font-size: .64rem !important;
          font-weight: 700 !important;
          color: #7A9BB0 !important;
          text-transform: uppercase !important;
          letter-spacing: .05em !important;
        }

        /* ── DatePicker ───────────────────────────────────────────── */
        .react-datepicker-wrapper { width: 100%; position: relative; }
        .react-datepicker__input-container { width: 100%; position: relative; }

        .custom-datepicker-input {
          border-radius: 10px !important;
          height: 36px !important;
          padding-right: 35px !important;
          padding-left: 12px !important;
          border: 1px solid rgba(42,101,145,.15) !important;
          width: 100% !important;
          font-family: 'Inter','Segoe UI',sans-serif !important;
          font-size: .78rem !important;
          background-color: white !important;
          color: #0F2D42 !important;
        }

        .custom-datepicker-input:focus {
          border-color: #2A6591 !important;
          outline: none !important;
          box-shadow: 0 0 0 3px rgba(42,101,145,.10) !important;
        }

        .custom-datepicker-input:disabled {
          background-color: #F0F6FB !important;
          cursor: not-allowed !important;
        }

        .custom-datepicker-input::placeholder {
          color: #7A9BB0;
          opacity: 0.7;
        }

        .custom-datepicker-input::-webkit-calendar-picker-indicator,
        .custom-datepicker-input::-webkit-inner-spin-button,
        .custom-datepicker-input::-webkit-outer-spin-button {
          display: none !important;
        }

        .react-datepicker__triangle,
        .react-datepicker-popper__arrow,
        .react-datepicker__triangle::before,
        .react-datepicker__triangle::after {
          display: none !important;
          visibility: hidden !important;
          border: none !important; width: 0 !important; height: 0 !important;
        }

        .react-datepicker__year-read-view--down-arrow,
        .react-datepicker__month-read-view--down-arrow,
        .react-datepicker__month-year-read-view--down-arrow { display: none !important; }

        .react-datepicker-popper { z-index: 10000 !important; position: absolute !important; }
        .react-datepicker-popper[data-placement^="bottom"] { margin-top: 8px !important; }
        .react-datepicker-popper[data-placement^="top"]    { margin-bottom: 8px !important; }

        .react-datepicker {
          font-family: 'Inter','Segoe UI',sans-serif !important;
          border: 1px solid rgba(42,101,145,.10) !important;
          border-radius: 16px !important;
          box-shadow: 0 10px 40px rgba(15,45,66,.14) !important;
          z-index: 10000 !important;
          background-color: white !important;
        }

        .react-datepicker__header {
          background-color: #F0F6FB !important;
          border-bottom: 1px solid rgba(42,101,145,.10) !important;
          border-radius: 16px 16px 0 0 !important;
          padding: 12px 0 8px !important;
        }

        .react-datepicker__current-month {
          color: #0F2D42 !important;
          font-weight: 700 !important;
          font-size: .82rem !important;
        }

        .react-datepicker__day-name {
          color: #7A9BB0 !important;
          font-weight: 600 !important;
          font-size: .68rem !important;
        }

        .react-datepicker__day {
          color: #0F2D42 !important;
          font-size: 13px !important;
          border-radius: 8px !important;
          margin: 2px !important;
        }

        .react-datepicker__day:hover {
          background-color: rgba(42,101,145,.12) !important;
          color: #2A6591 !important;
        }

        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background-color: #2A6591 !important;
          color: white !important;
          font-weight: 700 !important;
          border-radius: 8px !important;
        }

        .react-datepicker__day--disabled { color: #ccc !important; cursor: not-allowed !important; }
        .react-datepicker__day--outside-month { color: #ccc !important; }
        .react-datepicker__navigation { top: 12px !important; }
        .react-datepicker__navigation-icon::before { border-color: #7A9BB0 !important; border-width: 2px 2px 0 0 !important; }
        .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before { border-color: #2A6591 !important; }
        .react-datepicker__tab-loop { position: relative; }

        /* ── Filter Buttons ───────────────────────────────────────── */
        .filter-apply-btn {
          width: 100%;
          height: 36px;
          border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, #1C4E72 0%, #2A6591 100%);
          color: #ffffff;
          font-family: 'Inter','Segoe UI',sans-serif;
          font-weight: 700;
          font-size: .78rem;
          padding: 0 16px;
          transition: all 0.2s cubic-bezier(.16,1,.3,1);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(42,101,145,.18);
          letter-spacing: -0.01em;
        }

        .filter-apply-btn:hover {
          opacity: .9;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(42,101,145,.22);
        }

        .filter-apply-btn:active {
          transform: none;
        }

        .filter-reset-btn {
          width: 100%;
          height: 36px;
          border-radius: 10px;
          border: 1.5px solid rgba(42,101,145,.25);
          background-color: #FFFFFF;
          color: #2A6591;
          font-family: 'Inter','Segoe UI',sans-serif;
          font-weight: 700;
          font-size: .78rem;
          padding: 0 16px;
          transition: all 0.2s cubic-bezier(.16,1,.3,1);
          display: flex;
          align-items: center;
          justify-content: center;
          letter-spacing: -0.01em;
        }

        .filter-reset-btn:hover {
          background: rgba(42,101,145,.06);
          border-color: #2A6591;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(42,101,145,.10);
        }

        .filter-reset-btn:active {
          transform: none;
        }

        .filter-reset-btn i {
          color: inherit;
        }

        .filter-refresh-btn {
          width: 100%;
          height: 36px;
          border-radius: 10px;
          border: 1.5px solid rgba(42,101,145,.25);
          background-color: #2A6591;
          color: #FFFFFF !important;
          font-family: 'Inter','Segoe UI',sans-serif;
          font-weight: 700;
          font-size: .78rem;
          padding: 0 16px;
          transition: all 0.2s cubic-bezier(.16,1,.3,1);
          display: flex;
          align-items: center;
          justify-content: center;
          letter-spacing: -0.01em;
        }

        .filter-refresh-btn i,
        .filter-refresh-btn:hover i,
        .filter-refresh-btn:focus i {
          color: #FFFFFF !important;
        }

        .filter-refresh-btn:hover:not(:disabled) {
          background: #1e4f72;
          border-color: #1e4f72;
          color: #FFFFFF !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(42,101,145,.25);
        }

        .filter-refresh-btn:active:not(:disabled) {
          transform: none;
        }

        .filter-refresh-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        /* ── Table styling ────────────────────────────────────────── */
        .iccc__table--container {
           background-color: #FFFFFF !important;
           border: 1px solid rgba(42,101,145,.10) !important;
           box-shadow: 0 4px 12px rgba(15,45,66,.05) !important;
        }

        .iccc__table--container th,
        .iccc__table--container thead th,
        .iccc__table--container .table > :not(caption) > thead > tr > th {
          background: linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%) !important;
          color: #FFFFFF !important;
          font-family: 'Sora','Segoe UI',sans-serif !important;
          font-size: .72rem !important;
          font-weight: 700 !important;
          letter-spacing: -0.01em !important;
          padding: 10px 10px !important;
          white-space: nowrap !important;
          border-bottom: none !important;
        }

        .iccc__table--container td,
        .iccc__table--container .table > :not(caption) > tbody > tr > td {
          font-family: 'Inter','Segoe UI',sans-serif !important;
          font-size: .75rem !important;
          color: #0F2D42 !important;
          background-color: #FFFFFF !important;
          border-color: rgba(42,101,145,.08) !important;
          padding: 14px 14px !important;
          vertical-align: middle;
        }

        .iccc__table--container tr:hover td,
        .iccc__table--container tbody tr:hover > * {
          background-color: rgba(42,101,145,.035) !important;
        }
        
        .iccc__table--container tr {
          border-bottom: 1px solid rgba(42,101,145,.08) !important;
        }
      `}
      </style>
    </div>
  );
};

export default DisasterAlertHistoryTable;